import { Percent, Store, TrendingUp, Wallet } from 'lucide-react';

import ChartCard from '@/features/reports/components/ChartCard';
import RankBarChart from '@/features/reports/components/charts/RankBarChart';
import { SimpleKpi } from '@/features/reports/components/KpiTile';
import ReportTable from '@/features/reports/components/ReportTable';
import { useReportOutlet } from '@/features/reports/hooks/useReportOutlet';
import { useVendorPerformance } from '@/features/reports/hooks/useReports';
import {
  formatDecimal,
  formatNumber,
  formatPercent,
  formatRangeCaption,
  formatToman,
} from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';

const vendorLabel = (row) =>
  persianDisplayText(row.shop_name_fa, 'فروشندهٔ بدون نام');

/** Vendor leaderboard with commission split and share of platform revenue. */
export default function VendorsReport() {
  const { query, filters } = useReportOutlet();
  const vendors = useVendorPerformance(query, { limit: 50 });

  const rows = vendors.data?.rows ?? [];
  const totalRevenue = Number(vendors.data?.total_revenue ?? 0);

  const totalCommission = rows.reduce(
    (sum, row) => sum + Number(row.platform_commission ?? 0),
    0
  );
  const topVendor = rows[0];

  return (
    <div className="reports-page">
      <div className="report-section-head">
        <h2 className="section-title">فروشندگان</h2>
        <p className="report-section-caption">
          {formatRangeCaption(filters.from, filters.to)} • دید پلتفرمی
        </p>
      </div>

      <div className="kpi-grid report-kpi-grid">
        <SimpleKpi
          title="درآمد کل فروشندگان"
          value={totalRevenue}
          format={formatToman}
          icon={TrendingUp}
          accent="green"
        />
        <SimpleKpi
          title="فروشندگان فعال"
          value={rows.length}
          format={formatNumber}
          icon={Store}
          accent="blue"
        />
        <SimpleKpi
          title="کمیسیون پلتفرم"
          value={totalCommission}
          format={formatToman}
          icon={Wallet}
          accent="amber"
        />
        <SimpleKpi
          title="سهم فروشنده نخست"
          value={topVendor?.revenue_share_pct}
          format={(value) => formatPercent(value)}
          icon={Percent}
          accent="purple"
          secondary={topVendor ? vendorLabel(topVendor) : '—'}
        />
      </div>

      <ChartCard
        title="رتبه‌بندی فروشندگان"
        subtitle="۱۵ فروشنده نخست بر پایه درآمد"
        height={440}
        isLoading={vendors.isLoading}
        isError={vendors.isError}
        error={vendors.error}
        isEmpty={!rows.length}
      >
        <RankBarChart
          rows={rows.slice(0, 15).map((row) => ({ ...row, label: vendorLabel(row) }))}
          valueKey="revenue"
          money
          height={440}
          yAxisWidth={170}
        />
      </ChartCard>

      <ChartCard
        title="جدول فروشندگان"
        subtitle="درآمد، کالای فروش‌رفته، کمیسیون و سهم هر فروشنده"
        bodyClassName="report-card-body-table"
        isLoading={vendors.isLoading}
        isError={vendors.isError}
        error={vendors.error}
        isEmpty={!rows.length}
      >
        <ReportTable
          rows={rows}
          rowKey={(row) => row.vendor_id}
          maxHeight={620}
          columns={[
            {
              key: 'rank',
              header: '#',
              width: 48,
              render: (_row, index) => formatNumber(index + 1),
            },
            {
              key: 'shop',
              header: 'فروشنده',
              render: (row) => (
                <div className="report-cell-stack">
                  <span className="report-cell-primary">{vendorLabel(row)}</span>
                </div>
              ),
            },
            { key: 'revenue', header: 'درآمد', render: (row) => formatToman(row.revenue) },
            { key: 'units', header: 'کالا', render: (row) => formatNumber(row.units) },
            { key: 'orders', header: 'سفارش', render: (row) => formatNumber(row.orders) },
            {
              key: 'platform_commission',
              header: 'کمیسیون پلتفرم',
              render: (row) => formatToman(row.platform_commission),
            },
            {
              key: 'vendor_share',
              header: 'سهم فروشنده',
              render: (row) => formatToman(row.vendor_share),
            },
            {
              key: 'commission_rate',
              header: 'نرخ کمیسیون',
              render: (row) =>
                row.commission_rate === null || row.commission_rate === undefined ? (
                  <span className="report-cell-secondary">—</span>
                ) : (
                  formatDecimal(row.commission_rate, 1) + '٪'
                ),
            },
            {
              key: 'revenue_share_pct',
              header: 'سهم از کل',
              render: (row) => formatDecimal(row.revenue_share_pct, 1) + '٪',
            },
          ]}
        />
      </ChartCard>
    </div>
  );
}
